import { useFormContext, type FieldErrors } from "react-hook-form";
import { Alert, AlertDescription, AlertTitle } from "@bcc/components/ui/alert";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

interface ErrorSummaryProps {
  className?: string;
}

interface FlattenedError {
  path: string;
  message: string;
}

/**
 * Recursively flattens React Hook Form's nested FieldErrors object
 * into a flat array of { path, message } objects.
 *
 * Handles nested errors for variants (e.g., variants.0.bagSize → "Variant 1: Bag size is required")
 */
function flattenErrors(errors: FieldErrors, t: TFunction, parentPath: string = ""): FlattenedError[] {
  const result: FlattenedError[] = [];

  for (const [key, value] of Object.entries(errors)) {
    const currentPath = parentPath ? `${parentPath}.${key}` : key;

    // Handle array fields (variants)
    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (item && typeof item === "object") {
          const variantPrefix = t("views.shopifyImportModal.productForm.errorSummary.variantPrefix", {
            index: index + 1,
          }) as string;
          const nestedErrors = flattenErrors(item as FieldErrors, t, `${currentPath}.${index}`);
          nestedErrors.forEach((error) => {
            result.push({
              path: error.path,
              message: `${variantPrefix}: ${error.message}`,
            });
          });
        }
      });
    }
    // Handle nested objects
    else if (value && typeof value === "object" && "message" in value) {
      // Leaf error with message
      result.push({
        path: currentPath,
        message: value.message as string,
      });
    } else if (value && typeof value === "object") {
      // Nested object without message
      result.push(...flattenErrors(value as FieldErrors, t, currentPath));
    }
  }

  return result;
}

export function ErrorSummary({ className }: Readonly<ErrorSummaryProps>) {
  const {
    formState: { errors, isSubmitted },
  } = useFormContext();
  const { t } = useTranslation();

  // Only show after first submission attempt
  if (!isSubmitted) return null;

  // Flatten nested errors into a list of { path, message }
  const errorList = flattenErrors(errors, t);
  if (errorList.length === 0) return null;

  return (
    <Alert variant="destructive" className={className}>
      <AlertTitle>{t("views.shopifyImportModal.productForm.errorSummary.title")}</AlertTitle>
      <AlertDescription>
        <ul className="list-disc pl-4 mt-2 space-y-1">
          {errorList.map((error) => (
            <li key={error.path} className="text-sm">
              {error.message}
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}
